import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Container from './Container';
import Button from './Button';
import Navbar from './Navbar';
import Footer from './Footer';

const EmailVerification = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  // Toasts disabled per request; using only inline message boxes
  const showToast = () => {}; // intentionally empty

  // Check if user came from verification link
  const tokenHash = searchParams.get('token_hash') || searchParams.get('token');
  const type = searchParams.get('type');
  const errorDescription = searchParams.get('error_description');
  const verifiedFlag = searchParams.get('verified');
  const isAlreadyVerified = verifiedFlag === '1';

  const parseHashParams = () => {
    const hash = window.location.hash || '';
    const raw = hash.startsWith('#') ? hash.slice(1) : hash;
    const params = new URLSearchParams(raw);
    return {
      token: params.get('token') || params.get('token_hash') || null,
      type: params.get('type') || null,
      error_description: params.get('error_description') || null
    };
  };

  useEffect(() => {
    const { token: hashToken, type: hashType, error_description: hashErr } = parseHashParams();

    if (import.meta.env.DEV) console.log('EmailVerification: init', {
      tokenHashPresent: !!tokenHash,
      type,
      siteUrl: window.location.origin,
      VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
      errorDescription,
      hashTokenPresent: !!hashToken,
      hashType,
      hashErr,
      verifiedFlag
    });

    const err = errorDescription || hashErr;
    if (err) {
      setError('Η επιβεβαίωση απέτυχε: ' + err);
      showToast('Η επιβεβαίωση απέτυχε. ' + err, 'error');
      return;
    }

    // If we have a token in query or hash, verify explicitly
    const effectiveToken = tokenHash || hashToken;
    const effectiveType = (type || hashType || 'signup');

    if (effectiveToken && (effectiveType === 'signup' || effectiveType === 'email')) {
      verifyEmail(effectiveToken, effectiveType);
      return;
    }

    // No token and no error
    if (verifiedFlag === '1') {
      setMessage('Το email σας επιβεβαιώθηκε επιτυχώς! Μπορείτε τώρα να συνδεθείτε.');
      showToast('Το email σας επιβεβαιώθηκε επιτυχώς!', 'success');
      const t = setTimeout(() => navigate('/login'), 3000);
      return () => clearTimeout(t);
    }

    // Otherwise, idle state with instructions
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenHash, type, errorDescription, verifiedFlag]);

  // Toast notifications disabled; using inline boxes only


  const verifyEmail = async (overrideToken, overrideType) => {
    const finalToken = overrideToken || tokenHash;
    const finalType = overrideType || (type === 'email' ? 'email' : 'signup');

    if (!finalToken) {
      setError('Δεν βρέθηκε κωδικός επιβεβαίωσης.');
      return;
    }

    setIsVerifying(true);
    setError('');
    setMessage('');

    try {
      if (import.meta.env.DEV) console.log('EmailVerification: calling verifyOtp', { tokenHashPresent: !!finalToken, finalType });
      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: finalToken,
        type: finalType
      });
      if (import.meta.env.DEV) console.log('EmailVerification: verifyOtp result', { data, error });

      if (error) {
        throw error;
      }

      if (data.user) {
        setMessage('Το email σας επιβεβαιώθηκε επιτυχώς! Μπορείτε τώρα να συνδεθείτε.');
        showToast('Το email σας επιβεβαιώθηκε επιτυχώς!', 'success');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        // Fallback success if no error
        setMessage('Το email σας επιβεβαιώθηκε επιτυχώς!');
        showToast('Το email σας επιβεβαιώθηκε επιτυχώς!', 'success');
        setTimeout(() => navigate('/login'), 3000);
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError('Η επιβεβαίωση απέτυχε. Παρακαλώ δοκιμάστε ξανά ή επικοινωνήστε μαζί μας.');
      showToast('Η επιβεβαίωση απέτυχε. Δοκιμάστε ξανά.', 'error');
    } finally {
      setIsVerifying(false);
    }
  };

  const resendVerificationEmail = async () => {
    if (!email.trim()) {
      setError('Παρακαλώ εισάγετε το email σας.');
      return;
    }

    setIsResending(true);
    setError('');
    setMessage('');

    try {
      if (import.meta.env.DEV) console.log('EmailVerification: calling resend', { email: email.trim() });
      const { data, error } = await supabase.auth.resend({
        type: 'signup',
        email: email.trim(),
        options: {
          emailRedirectTo: `${window.location.origin}/login?email_confirmed=true`
        }
      });
      if (import.meta.env.DEV) console.log('EmailVerification: resend result', { data, error });

      if (error) {
        throw error;
      }

      setMessage('Το email επιβεβαίωσης στάλθηκε ξανά! Παρακαλώ ελέγξτε το inbox σας.');
      showToast('Το email επιβεβαίωσης στάλθηκε ξανά!', 'success');
    } catch (err) {
      console.error('Resend error:', err);
      const msg = 'Δεν ήταν δυνατή η αποστολή του email. ' + (err?.message || 'Παρακαλώ δοκιμάστε ξανά.');
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setIsResending(false);
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    resendVerificationEmail();
  };

  // If verifying, show loading
  if (isVerifying) {
    return (
      <div style={{ backgroundColor: '#1A202C', minHeight: '100vh' }}>
        <Navbar />
        <main style={{ padding: '5rem 0 6rem 0', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(800px 800px at 10% -10%, rgba(16,185,129,0.15), transparent), radial-gradient(800px 800px at 110% 10%, rgba(59,130,246,0.08), transparent)' }} />
          <Container>
            <div style={{ maxWidth: '560px', margin: '0 auto' }}>
              <div style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.35), rgba(59,130,246,0.25))', borderRadius: '1rem', padding: '1px' }}>
                <div style={{ backgroundColor: '#2d3748', borderRadius: '1rem', padding: '2rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.25rem', textAlign: 'center' }}>
                    <div style={{ width: '3.5rem', height: '3.5rem', border: '4px solid #4a5568', borderTop: '4px solid #10B981', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                    <h2 style={{ color: '#f7fafc', fontSize: '1.5rem', fontWeight: 'bold' }}>Επιβεβαίωση Email...</h2>
                    <p style={{ color: '#a0aec0', fontSize: '1rem' }}>Παρακαλώ περιμένετε ενώ επιβεβαιώνουμε το email σας.</p>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#1A202C', minHeight: '100vh' }}>
      <Navbar />
      <main style={{ padding: '5rem 0 6rem 0', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(800px 800px at 10% -10%, rgba(16,185,129,0.15), transparent), radial-gradient(800px 800px at 110% 10%, rgba(59,130,246,0.08), transparent)' }} />
        <Container>
          <div style={{ maxWidth: '640px', margin: '0 auto', position: 'relative' }}>
            <div style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.35), rgba(59,130,246,0.25))', borderRadius: '1rem', padding: '1px' }}>
              <div style={{ backgroundColor: '#2d3748', borderRadius: '1rem', padding: '2.25rem' }}>
                {/* Header */}
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                  <div style={{ width: '4rem', height: '4rem', backgroundColor: '#10B981', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem', fontSize: '2rem' }}>✉️</div>
                  <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f7fafc', marginBottom: '0.5rem' }}>Επιβεβαίωση Email</h1>
                  <p style={{ fontSize: '1.05rem', color: '#a0aec0', lineHeight: '1.6' }}>Έχουμε στείλει ένα email επιβεβαίωσης στη διεύθυνση που δώσατε.</p>
                </div>

                {/* Success Message */}
                {message && (
                  <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#065f46', border: '1px solid #10B981', borderRadius: '0.5rem', color: '#d1fae5' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {message}
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#991b1b', border: '1px solid #dc2626', borderRadius: '0.5rem', color: '#fecaca' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {error}
                    </div>
                  </div>
                )}

                {/* Instructions */}
                <div style={{ backgroundColor: '#374151', padding: '1.25rem', borderRadius: '0.5rem', marginBottom: '1.5rem' }}>
                  <h3 style={{ color: '#f7fafc', fontSize: '1.05rem', fontWeight: 600, marginBottom: '0.75rem' }}>Τι να κάνετε:</h3>
                  <ol style={{ color: '#d1d5db', fontSize: '1rem', lineHeight: '1.6', paddingLeft: '1.25rem' }}>
                    <li>Ελέγξτε το inbox του email σας</li>
                    <li>Αναζητήστε για emails από το "TailClinic"</li>
                    <li>Κάντε κλικ στο link επιβεβαίωσης</li>
                    <li>Επιστρέψτε εδώ για να συνδεθείτε</li>
                  </ol>
                </div>

                {/* Resend Email Form */}
                <div style={{ borderTop: '1px solid #4a5568', paddingTop: '1.5rem', marginTop: '1.5rem' }}>
                  <h3 style={{ color: '#f7fafc', fontSize: '1.05rem', fontWeight: 600, marginBottom: '0.75rem' }}>Δεν λάβατε το email;</h3>
                 {isAlreadyVerified && (
                   <div style={{ marginBottom: '0.75rem', padding: '0.75rem', backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '0.5rem', color: '#9ca3af', fontSize: '0.95rem' }}>
                     Το email σας έχει ήδη επιβεβαιωθεί. Δεν θα σταλεί νέο email επιβεβαίωσης.
                   </div>
                 )}
                  <form onSubmit={handleEmailSubmit} style={{ display: 'flex', gap: '1rem' }}>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Εισάγετε το email σας"
                      disabled={isResending || isAlreadyVerified}
                      style={{ flex: 1, padding: '0.75rem 1rem', backgroundColor: '#4a5568', border: '1px solid #6b7280', borderRadius: '0.5rem', color: '#f7fafc', fontSize: '1rem', outline: 'none', opacity: isResending ? 0.6 : 1 }}
                    />
                    <Button type="submit" variant="primary" disabled={isResending || isAlreadyVerified} style={{ whiteSpace: 'nowrap', opacity: (isResending || isAlreadyVerified) ? 0.6 : 1 }}>
                      {isResending ? 'Αποστολή...' : 'Επαναποστολή'}
                    </Button>
                  </form>
                </div>

                {/* Back to Login */}
                <div style={{ textAlign: 'center', marginTop: '1.75rem', paddingTop: '1.75rem', borderTop: '1px solid #4a5568' }}>
                  <p style={{ color: '#a0aec0', fontSize: '1rem', marginBottom: '0.75rem' }}>Έχετε ήδη επιβεβαιώσει το email σας;</p>
                  <Button variant="outline" onClick={() => navigate('/login')} style={{ minWidth: '150px' }}>Σύνδεση</Button>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </main>
      <Footer />


    </div>
  );
};

export default EmailVerification; 